import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { updateMetaTags } from '../utils/seo'

const MacBookAkkuTauschenPage: React.FC = () => {
  const navigate = useNavigate()
  const go = (path: string) => { navigate(path); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50) }

  useEffect(() => {
    updateMetaTags({
      title: 'MacBook Akku tauschen – Alle Modelle & Preise ab 129€ | PhoneMedic',
      description: 'MacBook Akku tauschen ✓ MacBook Pro & Air alle Modelle ✓ ab 129€ ✓ Original Apple Qualität ✓ 12 Monate Garantie ✓ Bundesweiter Einsende-Service | PhoneMedic',
      keywords: ['MacBook Akku tauschen', 'MacBook Akku wechseln', 'MacBook Akku Kosten', 'MacBook Pro Akku', 'MacBook Air Akku', 'MacBook Akku aufgebläht'],
      canonicalUrl: 'https://www.phone-medic.de/macbook-akku-tauschen',
    })

    const schema = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'MacBook Akku tauschen',
        description: 'Professioneller Akkutausch für alle MacBook Pro und MacBook Air Modelle. Original Qualität, 12 Monate Garantie.',
        provider: { '@type': 'LocalBusiness', name: 'PhoneMedic', url: 'https://www.phone-medic.de', telephone: '+491605010907' },
        offers: [
          { '@type': 'Offer', name: 'MacBook Air 13" Akku', price: '129.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
          { '@type': 'Offer', name: 'MacBook Pro 13" Akku', price: '149.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
          { '@type': 'Offer', name: 'MacBook Pro 14" Akku', price: '179.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
          { '@type': 'Offer', name: 'MacBook Pro 16" Akku', price: '199.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
        ],
        areaServed: 'DE',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Wann sollte ich den MacBook Akku tauschen?',
            acceptedAnswer: { '@type': 'Answer', text: 'Der Akku sollte getauscht werden wenn: die Akku-Gesundheit unter 80% liegt, das MacBook weniger als 2 Stunden hält, der Akku aufgebläht ist (Tastatur wölbt sich), das MacBook bei niedriger Ladestand plötzlich ausgeht, oder die Meldung "Akku warten" erscheint.' },
          },
          {
            '@type': 'Question',
            name: 'Was kostet ein MacBook Akkutausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Der Preis hängt vom Modell ab: MacBook Air 13" ab 129€, MacBook Pro 13" ab 149€, MacBook Pro 14" ab 179€, MacBook Pro 16" ab 199€, MacBook Air 15" ab 169€.' },
          },
          {
            '@type': 'Question',
            name: 'Wie viele Ladezyklen hat ein MacBook Akku?',
            acceptedAnswer: { '@type': 'Answer', text: 'Apple gibt für die meisten MacBook Akkus 1.000 Ladezyklen an, bevor die Kapazität auf 80% sinkt. Die Akku-Zyklen kannst du in den Systeminformationen unter "Stromversorgung" ablesen.' },
          },
          {
            '@type': 'Question',
            name: 'Gehen bei einem Akkutausch Daten verloren?',
            acceptedAnswer: { '@type': 'Answer', text: 'Nein. Beim Akkutausch wird nur der Akku ausgetauscht. Die SSD mit deinen Daten bleibt vollständig unberührt.' },
          },
          {
            '@type': 'Question',
            name: 'Wie lange dauert ein MacBook Akkutausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Der eigentliche Akkutausch dauert 1–2 Stunden. Inklusive Versandzeit (Hin- und Rückversand) rechne mit 3–5 Werktagen.' },
          },
        ],
      },
    ]

    let script = document.querySelector('#macbook-akku-schema') as HTMLScriptElement
    if (!script) {
      script = document.createElement('script')
      script.id = 'macbook-akku-schema'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(schema)
    return () => { document.querySelector('#macbook-akku-schema')?.remove() }
  }, [])

  const prices = [
    { model: 'MacBook Air 13" (M1, M2, M3)', price: 'ab 129 €', note: '' },
    { model: 'MacBook Air 15" (M2, M3)', price: 'ab 169 €', note: '' },
    { model: 'MacBook Pro 13" (M1, M2, Intel)', price: 'ab 149 €', note: '' },
    { model: 'MacBook Pro 14" (M1, M2, M3, M4)', price: 'ab 179 €', note: '' },
    { model: 'MacBook Pro 16" (M1, M2, M3, M4)', price: 'ab 199 €', note: '' },
    { model: 'MacBook Pro 13" (Intel, 2016–2020)', price: 'ab 129 €', note: '' },
    { model: 'MacBook Pro 15" (Intel)', price: 'ab 179 €', note: '' },
    { model: 'MacBook (12", 2015–2019)', price: 'ab 119 €', note: '' },
  ]

  const symptoms = [
    { icon: '⏱️', title: 'Kurze Akkulaufzeit', desc: 'Das MacBook hält nur noch 1–2 Stunden statt 8–12 Stunden. Ein klares Zeichen für nachlassende Akkukapazität.' },
    { icon: '📊', title: 'Akkugesundheit unter 80%', desc: 'Unter Systemeinstellungen → Batterie → Batteriestatus kannst du die Akkugesundheit prüfen. Unter 80% ist ein Tausch sinnvoll.' },
    { icon: '💥', title: 'Plötzlicher Ausfall', desc: 'Das MacBook schaltet bei 30% oder 20% plötzlich ab? Der Akku kann die nötige Spannung nicht mehr liefern.' },
    { icon: '⬆️', title: 'Aufgeblähter Akku', desc: 'Wölbt sich die Tastatur oder das Trackpad nach oben? Ein aufgeblähter Akku ist gefährlich und muss sofort getauscht werden.' },
    { icon: '⚠️', title: '"Akku warten" Meldung', desc: 'macOS zeigt diese Meldung wenn der Akku die Mindestkapazität unterschreitet oder ungewöhnliches Verhalten zeigt.' },
    { icon: '🔌', title: 'Nur mit Ladekabel benutzbar', desc: 'Das MacBook startet ohne Ladekabel gar nicht mehr oder fährt sofort herunter? Der Akku ist komplett defekt.' },
  ]

  const faqs = [
    { q: 'Wann sollte ich den MacBook Akku tauschen?', a: 'Wenn die Akku-Gesundheit unter 80% liegt, das MacBook weniger als 2 Stunden hält, der Akku aufgebläht ist oder die Meldung "Akku warten" erscheint.' },
    { q: 'Was kostet ein MacBook Akkutausch?', a: 'Je nach Modell: MacBook Air 13" ab 129€, Pro 13" ab 149€, Pro 14" ab 179€, Pro 16" ab 199€. Genaue Preise für dein Modell auf Anfrage.' },
    { q: 'Wie viele Ladezyklen hat ein MacBook Akku?', a: 'Apple gibt für die meisten Modelle 1.000 Ladezyklen an, bevor die Kapazität auf 80% sinkt. Die Zyklen kannst du unter Systeminformationen → Stromversorgung ablesen.' },
    { q: 'Gehen bei einem Akkutausch Daten verloren?', a: 'Nein. Beim Akkutausch wird nur der Akku gewechselt. Die SSD mit deinen Daten bleibt vollständig unberührt.' },
    { q: 'Wie lange dauert ein MacBook Akkutausch?', a: 'Der eigentliche Tausch dauert 1–2 Stunden. Inklusive Versand rechne mit 3–5 Werktagen.' },
    { q: 'Kann Apple den Akku tauschen?', a: 'Ja, aber Apple verlangt oft deutlich mehr. Bei neueren Modellen mit verklebten Akkus kann der Preis 200–350 € betragen. Wir bieten dieselbe Qualität ab 129 €.' },
  ]

  return (
    <div className="min-h-screen bg-white">
      <section
        className="relative text-white py-20"
        style={{ backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center top' }}
      >
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-sm font-medium px-3 py-1 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></span>
              MacBook Pro & Air · alle Modelle
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              MacBook Akku tauschen –<br />
              <span className="text-indigo-400">Alle Modelle & Preise</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              MacBook hält nicht mehr lange oder Akku aufgebläht? Wir tauschen Akkus für alle MacBook Pro und Air Modelle – professionell, mit 12 Monaten Garantie, bundesweiter Einsende-Service.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="mailto:service@phone-medic.de" className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
                Akku tauschen lassen
              </a>
              <button onClick={() => go('/macbook-reparatur?tab=reparatur')} className="bg-white/10 text-white border border-white/20 px-7 py-3.5 rounded-lg font-semibold hover:bg-white/20 transition-colors">
                Modell & Preis wählen
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-800 text-white py-5 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { number: 'ab 129 €', label: 'Akkutausch' },
              { number: '1–2h', label: 'Reparaturzeit' },
              { number: '12 Monate', label: 'Garantie' },
              { number: 'Bundesweit', label: 'Einsende-Service' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-2xl font-bold text-indigo-400">{s.number}</div>
                <div className="text-sm text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Wann sollte der Akku getauscht werden?</h2>
            <p className="text-gray-500">Diese Symptome zeigen, dass dein MacBook einen neuen Akku braucht</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {symptoms.map((s, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">So läuft ein MacBook Akkutausch ab</h2>
            <p className="text-gray-500">Einblick in unsere professionelle Reparatur</p>
          </div>
          <div className="relative w-full rounded-xl overflow-hidden shadow-lg" style={{ paddingBottom: '56.25%', minHeight: '300px' }}>
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube.com/embed/VuTgfnkQlcE"
              title="MacBook Akkutausch – PhoneMedic"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Preise nach Modell</h2>
            <p className="text-gray-500">Alle Preise inklusive Einbau und 12 Monate Garantie</p>
          </div>
          <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-2 bg-gray-800 text-white px-6 py-3 text-sm font-semibold">
              <span>Modell</span>
              <span className="text-right">Preis</span>
            </div>
            {prices.map((p, i) => (
              <div key={i} className={`grid grid-cols-2 px-6 py-4 text-sm border-b border-gray-200 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                <span className="text-gray-700 font-medium">{p.model}</span>
                <span className="text-right font-bold text-indigo-600">{p.price}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3 text-center">Dein Modell nicht dabei? Einfach per E-Mail anfragen – wir reparieren alle MacBook Modelle.</p>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Weitere MacBook Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'MacBook Display Reparatur', path: '/macbook-display-reparatur' },
              { label: 'MacBook startet nicht', path: '/macbook-startet-nicht' },
              { label: 'MacBook Wasserschaden', path: '/macbook-wasserschaden' },
              { label: 'Logicboard Reparatur', path: '/macbook-logicboard-reparatur' },
            ].map((item) => (
              <button key={item.path} onClick={() => go(item.path)} className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm font-medium text-gray-700 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors text-center">
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">MacBook Akku tauschen lassen</h2>
          <p className="text-gray-400 mb-8">Alle Modelle · ab 129 € · 12 Monate Garantie · Bundesweiter Einsende-Service</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:service@phone-medic.de" className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
              Per E-Mail anfragen
            </a>
            <button onClick={() => go('/macbook-reparatur?tab=reparatur')} className="bg-white text-gray-900 px-7 py-3.5 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Modell auswählen & buchen
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default MacBookAkkuTauschenPage
